/* eslint-disable max-len */
import { Locale, Stage } from 'corgi-types';

interface GetConstructorBeaconScriptOptions {
  stage: Stage;
  locale: Locale;
  noThirdPartyScripts: boolean;
  crossDeviceHash?: string;
  constructorTestCell?: string;
}

// We are hardcoding the constructor indexes to simplify the Frontend implementation.
// See https://home24.atlassian.net/browse/CPT-3948?focusedCommentId=2999438 for more details.
const CONSTRUCTOR_INDEX: Partial<Record<Stage, Record<Locale, string>>> = {
  // Based on: https://github.com/Home24/GraphQL-Api/blob/ae1055b2b1f057b72cc2e219c80db5da4baad94e/configs/production.toml#L171
  [Stage.Production]: {
    [Locale.DE_DE]: 'key_oqZIqFWqcybfDPxx',
    [Locale.FR_FR]: 'key_CubMvZk1gJZyYSpq',
    [Locale.NL_NL]: 'key_Qjp1euxFI0RULsu7',
    [Locale.DE_AT]: 'key_XxglOzgk1wJtuuwu',
    [Locale.DE_CH]: 'key_R0GUvWkkJJBXZeHZ',
    [Locale.FR_CH]: 'key_XgLZg2Y1erQ1E1x0',
    [Locale.NL_BE]: 'key_u7qsV2o8yILRG6Iy',
    [Locale.FR_BE]: 'key_m893bHkfU6QkAVGv',
    [Locale.IT_IT]: 'key_O2RkRqFrRYbvYl4D',
  },
  // We currently don't use staging map, as we don't render the Beacon there.
  // However, the map is already provided - if we decide to change this in the future.
  // Based on: https://github.com/Home24/GraphQL-Api/blob/ae1055b2b1f057b72cc2e219c80db5da4baad94e/configs/staging.toml#L173
  [Stage.Staging]: {
    [Locale.DE_DE]: 'key_rZEEH8GBz77gcRWx',
    [Locale.FR_FR]: 'key_BJglBCqdnJZUAS8e',
    [Locale.NL_NL]: 'key_RPD05Vf9L0bIkaFA',
    [Locale.DE_AT]: 'key_At50EDIOrg4hzwIf',
    [Locale.DE_CH]: 'key_DdFUJBNp5HgiRTmX',
    [Locale.FR_CH]: 'key_o5uJDvjgUYMOeKWP',
    [Locale.NL_BE]: 'key_BZj29VS12wJKHORB',
    [Locale.FR_BE]: 'key_jzIogMBP6fjlyYHf',
    [Locale.IT_IT]: 'key_Pd3aHNxtdeuXmvTr',
  },
};

const getConstructorIndexKey = ({
  stage,
  locale,
}: Pick<GetConstructorBeaconScriptOptions, 'stage' | 'locale'>) =>
  CONSTRUCTOR_INDEX[stage]?.[locale];

const buildInlineScript = ({
  stage,
  locale,
  crossDeviceHash,
  constructorTestCell,
}: Pick<
  GetConstructorBeaconScriptOptions,
  'stage' | 'locale' | 'crossDeviceHash' | 'constructorTestCell'
>) => {
  const crossDeviceValue = crossDeviceHash ? JSON.stringify(crossDeviceHash) : 'null';
  const indexKey = getConstructorIndexKey({ stage, locale });

  const parts: string[] = [
    // @deprecated Use window.cnstrc.userId instead
    `window.h24CrossDeviceId = ${crossDeviceValue};`,
    'window.cnstrc = window.cnstrc || {};',
    `window.cnstrc.userId = ${crossDeviceValue};`,
    `window.cnstrc.indexKey = ${JSON.stringify(indexKey)};`,
  ];

  if (constructorTestCell) {
    parts.push(`window.cnstrc.testCell = ${JSON.stringify(constructorTestCell)};`);
  }

  return parts.join(' ');
};

const shouldRenderConstructorBeaconScript = ({
  stage,
  noThirdPartyScripts,
}: Omit<GetConstructorBeaconScriptOptions, 'locale' | 'crossDeviceHash' | 'constructorTestCell'>) =>
  stage === Stage.Production && !noThirdPartyScripts;

export const getConstructorBeaconScriptOptions = ({
  stage,
  locale,
  crossDeviceHash,
  constructorTestCell,
  ...rest
}: GetConstructorBeaconScriptOptions) => {
  if (shouldRenderConstructorBeaconScript({ stage, ...rest })) {
    return [
      {
        script: buildInlineScript({
          stage,
          locale,
          crossDeviceHash,
          constructorTestCell,
        }),
        scriptParams: {},
      },
      {
        script: '',
        scriptParams: {
          src: 'https://cnstrc.com/js/cust/home24_asX-mO.js',
        },
      },
    ];
  }
  return null;
};
